import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
  ButtonGroup,
  IconButton,
  HStack,
  Switch,
  VStack,
  RadioGroup,
  Radio,
  useToast,
  Badge,
} from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import {
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiDeleteBinLine,
  RiEyeLine,
  RiLoginCircleLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';
import axios from 'axios';
import {
  IBookingListCheckin,
  IBookingListItem,
} from '../../services/Bookings/ListBookingsService';
import { ConfirmationModal } from '../ConfirmationModal';
import { LinkButton } from '../LinkButton';
import { Pagination } from '../Pagination';
import { TableFilters } from '../TableFilters';
import { createBookingCheckinsService } from '../../services/Bookings/CreateBookingCheckinsService';
import { translateError } from '../../utils/errors';
import { maskDateTime } from '../../utils/formatters/handleMask';
import { useAuth } from '../../hooks/auth';
import { BookingPaymentConfirmationModal } from '../BookingPaymentConfirmationModal';
import {
  IBookingSessionBlockBookingBase,
  IBookingPaymentBase,
  PrettyBookingPaymentStatus,
} from '../../models/bookings';
import cancelBookingsService from '../../services/Bookings/CancelBookingsService';
import { IHandleBookingPayment } from '../../pages/App/General/Activities/ActivityDetails/components/ActivityDetailsData/components/ActivityDetailsTabs/components/ActivityCalendar/components/ScheduleBookingsModal/components/ScheduleTabs/components/ScheduleBookingsTable';
import { createBookingExternalPaymentsService } from '../../services/Bookings/CreateBookingExternalPaymentsService';
import { confirmSessionBlockBookingsService } from '../../services/Activities/ConfirmSessionBlockBookingsService';
import { declineSessionBlockBookingsService } from '../../services/Activities/DeclineSessionBlockBookingsService';
import { IAffectedSessionBlockReservation } from '../../services/Activities/ListAffectedSessionBlockReservationsService';
import { WaveSideEnum } from '../../models/activities';

type SelectOption = {
  label: string;
  value: string;
};

export interface IBookingTableItemCheckin extends IBookingListCheckin {
  formattedCreatedAt: string;
}

export interface IBookingTableItem extends IBookingListItem {
  checkins: IBookingTableItemCheckin[];
  formattedBookedDate: string;
  formattedCreatedAt: string;
  formattedDeletedAt?: string;
  formattedEndTime: string;
  formattedStartTime: string;
  isCancelable: boolean;
}

const sessionBlockBookingStatusLabels = {
  SUSPENDED: 'Sessão paralisada',
  PENDING_CONFIRMATION: 'Aguardando confirmação',
  CONFIRMED_BY_USER: 'Confirmada após paralisação',
  DECLINED_BY_USER: 'Recusada após paralisação',
  MANUAL_HANDLING: 'Em atendimento',
  RELEASED: 'Fila liberada',
};

const sessionBlockBookingStatusColorSchemes = {
  SUSPENDED: 'orange',
  PENDING_CONFIRMATION: 'yellow',
  CONFIRMED_BY_USER: 'green',
  DECLINED_BY_USER: 'red',
  MANUAL_HANDLING: 'purple',
  RELEASED: 'green',
};

const sessionBlockReasonLabels = {
  WEATHER_CONDITIONS: 'Condições climáticas',
  POWER_OUTAGE: 'Falta de energia',
  TECHNICAL_ISSUES: 'Problemas técnicos',
  OTHER: 'Outros',
};

const getSessionBlockReason = (
  sessionBlockBooking?: IBookingSessionBlockBookingBase,
): string | null | undefined => {
  if (!sessionBlockBooking) {
    return undefined;
  }

  if (sessionBlockBooking.block.reason === 'OTHER') {
    return sessionBlockBooking.block.customReason;
  }

  return sessionBlockReasonLabels[sessionBlockBooking.block.reason];
};

const formatSessionBlockReservationDate = (
  reservation: IAffectedSessionBlockReservation,
): string => {
  const [year, month, day] = reservation.reservation.bookedDate.split('-');

  return `${day}/${month}/${year}`;
};

const formatSessionBlockReservationTime = (value: string): string =>
  value.includes('T') ? value.slice(11, 16) : value.slice(0, 5);

const formatPrice = (price?: number | null): string =>
  price
    ? (price / 100).toLocaleString('pt-BR', {
        currency: 'BRL',
        style: 'currency',
      })
    : 'Isento';

const formatSessionBlockCreatedAt = (
  reservation: IAffectedSessionBlockReservation,
): string => maskDateTime(reservation.reservation.createdAt);

interface IBookingTableProps extends BoxProps {
  bookings: IBookingTableItem[];
  currentPage?: number;
  filterBy?: string;
  filterByOptions?: SelectOption[];
  onChangeFilterBy?: (value: string) => void;
  onCheckin?: (bookingCheckin: IBookingTableItemCheckin) => void;
  onDeleteBooking: (booking: IBookingTableItem) => void;
  onFilter?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onPageChange?: (page: number) => void;
  onPay?: (data: IBookingPaymentBase) => void;
  onSessionBlockBookingUpdate?: (
    bookingId: string,
    sessionBlockBooking: IBookingSessionBlockBookingBase,
  ) => void;
  onSessionBlockReservationUpdate?: (
    reservationId: string,
    sessionBlockBooking: IBookingSessionBlockBookingBase,
  ) => void;
  onSort?: (sort: 'bookedDate' | 'createdAt') => void;
  onShowSessionBlocks?: () => void;
  onShowPaymentPending: () => void;
  onShowPending: () => void;
  onToggleBookingsTypeFilter?: (
    bookingsType: 'memberBookings' | 'guestsBookings',
  ) => void;
  toggleShowMyBookingsOnly?: () => void;
  order?: 'ASC' | 'DESC';
  showActivityColumn?: boolean;
  showMemberColumns?: boolean;
  showMyBookingsOnly?: boolean;
  showPaymentPending: boolean;
  showPending: boolean;
  showSessionBlocks?: boolean;
  sessionBlockReservations?: IAffectedSessionBlockReservation[];
  sortBy?: string;
  sortByOptions?: SelectOption[];
  totalPages?: number;
}

export const BookingTable = ({
  bookings,
  currentPage,
  filterBy,
  onShowPaymentPending,
  showPaymentPending,
  filterByOptions = [
    {
      label: 'Nome da atividade',
      value: 'activityName',
    },
    {
      label: 'Nome do local',
      value: 'spotName',
    },
    {
      label: 'Nome',
      value: 'userName',
    },
    {
      label: 'Documento',
      value: 'userDocument',
    },
    {
      label: 'Horário reservado',
      value: 'bookedDate',
    },
  ],
  onChangeFilterBy,
  onCheckin,
  onDeleteBooking,
  onFilter,
  onOrder,
  onPageChange,
  onPay,
  onSessionBlockBookingUpdate,
  onSessionBlockReservationUpdate,
  onSort,
  onShowSessionBlocks,
  onShowPending,
  onToggleBookingsTypeFilter,
  order,
  showActivityColumn = true,
  showMemberColumns = true,
  showPending,
  showSessionBlocks = false,
  sessionBlockReservations = [],
  showMyBookingsOnly,
  toggleShowMyBookingsOnly,
  sortBy,
  sortByOptions = [
    {
      label: 'Horário reservado',
      value: 'bookedDate',
    },
    {
      label: 'Reservado em',
      value: 'createdAt',
    },
  ],
  totalPages,
  ...rest
}: IBookingTableProps): JSX.Element => {
  const toast = useToast();

  const {
    venture: authenticatedVenture,
    assignment: authenticatedAssignment,
    user: authenticatedUser,
    experienceLevelsTitleMap,
  } = useAuth();

  const [handlingBooking, setHandlingBooking] = useState<IBookingTableItem>();
  const [bookingsTypeFilter, setBookingsTypeFilter] = useState<
    'memberBookings' | 'guestsBookings'
  >('memberBookings');
  const [
    isCheckinConfirmationModalVisible,
    setIsCheckinConfirmationModalVisible,
  ] = useState(false);
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);

  const cancelBookingWarningMessage = useMemo(() => {
    if (!handlingBooking) {
      return undefined;
    }

    if (handlingBooking?.payment?.status === 'PAYMENT_EXTERNAL') {
      return 'Essa reserva foi paga no clube, o reembolso deverá ser feito externamente.';
    }

    return undefined;
  }, [handlingBooking]);

  const handleTooggleCheckinConfirmationModal = useCallback(() => {
    setIsCheckinConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const togglePaymentModal = useCallback((booking?: IBookingTableItem) => {
    setHandlingBooking(booking);

    setIsPaymentModalVisible((prevState) => !prevState);
  }, []);

  const toggleDeleteConfirmationModal = (booking?: IBookingTableItem): void => {
    setHandlingBooking(booking);

    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  };

  const handleBookingPayment = useCallback(
    async ({ bookingId, payment }: IHandleBookingPayment) => {
      if (!onPay) {
        return;
      }

      try {
        const paidBooking = await createBookingExternalPaymentsService({
          bookingId,
          payment,
        });

        if (paidBooking.payment) {
          onPay(paidBooking.payment);
        }

        togglePaymentModal(undefined);

        toast({
          title: 'Transação concluída com sucesso',
          description: 'A transação foi efetuada corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          if (err.response?.data.message === 'user-address-missing') {
            toast({
              title: 'Cadastro incompleto',
              description: `Para registrar o pagamento será necessário completar o cadastro do membro. ${translateError(
                { message: err.response?.data.message },
              )}`,
              status: 'warning',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });

            return;
          }

          toast({
            title: 'Falha na transação',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao efetuar a transação, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [togglePaymentModal, onPay, toast],
  );

  const handleCreateCheckin = async (): Promise<void> => {
    if (handlingBooking && !!onCheckin) {
      try {
        const checkin = await createBookingCheckinsService(handlingBooking.id);

        handleTooggleCheckinConfirmationModal();

        onCheckin({
          ...checkin,
          formattedCreatedAt: maskDateTime(checkin.createdAt),
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao cadastrar',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao cadastrar o checkin, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }
  };

  const handleCloseBookingConfirmationModal = (): void => {
    togglePaymentModal(undefined);
  };

  const handleToggleBookingsTypeFilter = useCallback(
    (value: 'memberBookings' | 'guestsBookings') => {
      setBookingsTypeFilter(value);

      if (onToggleBookingsTypeFilter) {
        onToggleBookingsTypeFilter(value);
      }
    },
    [onToggleBookingsTypeFilter],
  );

  const deleteBooking = async (
    booking: IBookingTableItem,
    reason?: string,
  ): Promise<void> => {
    try {
      await cancelBookingsService({ bookingId: booking.id, reason });

      onDeleteBooking(booking);

      toast({
        title: 'Cancelada com sucesso',
        description: 'A reserva foi cancelada corretamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao cancelar',
          description:
            err.response?.data.metaData?.description ||
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao cancelar a reserva, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    } finally {
      toggleDeleteConfirmationModal(undefined);
    }
  };

  const handleConfirmSessionBlockBooking = async (
    booking: IBookingTableItem,
    sessionBlockBooking: IBookingSessionBlockBookingBase,
  ): Promise<void> => {
    try {
      const updatedSessionBlockBooking =
        await confirmSessionBlockBookingsService({
          blockId: sessionBlockBooking.blockId,
          bookingId: booking.id,
        });

      onSessionBlockBookingUpdate?.(booking.id, updatedSessionBlockBooking);

      toast({
        title: 'Reserva confirmada',
        description: 'Sua reserva foi confirmada corretamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao confirmar',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao confirmar a reserva, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  };

  const handleDeclineSessionBlockBooking = async (
    booking: IBookingTableItem,
    sessionBlockBooking: IBookingSessionBlockBookingBase,
  ): Promise<void> => {
    try {
      const updatedSessionBlockBooking =
        await declineSessionBlockBookingsService({
          blockId: sessionBlockBooking.blockId,
          bookingId: booking.id,
        });

      onSessionBlockBookingUpdate?.(booking.id, updatedSessionBlockBooking);

      toast({
        title: 'Reserva recusada',
        description: 'A reserva foi cancelada sem prejuízo.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao recusar',
          description:
            err.response?.data.metaData?.description ||
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao recusar a reserva, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  };

  const handleConfirmAffectedReservation = async (
    reservation: IAffectedSessionBlockReservation,
  ): Promise<void> => {
    try {
      const updatedSessionBlockBooking =
        await confirmSessionBlockBookingsService({
          blockId: reservation.block.id,
          bookingId: reservation.reservation.id,
        });

      onSessionBlockReservationUpdate?.(
        reservation.reservation.id,
        updatedSessionBlockBooking,
      );

      toast({
        title: 'Reserva confirmada',
        description: 'Sua reserva foi confirmada corretamente.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao confirmar',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao confirmar a reserva, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  };

  const handleDeclineAffectedReservation = async (
    reservation: IAffectedSessionBlockReservation,
  ): Promise<void> => {
    try {
      const updatedSessionBlockBooking =
        await declineSessionBlockBookingsService({
          blockId: reservation.block.id,
          bookingId: reservation.reservation.id,
        });

      onSessionBlockReservationUpdate?.(
        reservation.reservation.id,
        updatedSessionBlockBooking,
      );

      toast({
        title: 'Reserva recusada',
        description: 'A reserva foi cancelada sem prejuízo.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        variant: 'subtle',
        position: 'top-right',
      });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao recusar',
          description:
            err.response?.data.metaData?.description ||
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao recusar a reserva, tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    }
  };

  return (
    <Box {...rest}>
      {handlingBooking && (
        <ConfirmationModal
          isOpen={isCheckinConfirmationModalVisible}
          onClose={handleTooggleCheckinConfirmationModal}
          onConfirm={handleCreateCheckin}
          title="Confirmar checkin"
          message="Deseja realmente criar um checkin para essa reserva?"
        />
      )}

      {!!handlingBooking?.payment?.status && !!onPay && (
        <BookingPaymentConfirmationModal
          isOpen={isPaymentModalVisible}
          onClose={handleCloseBookingConfirmationModal}
          onConfirm={(payment) =>
            handleBookingPayment({
              bookingId: handlingBooking.id,
              payment,
            })
          }
          payAfterDisabled={
            handlingBooking.payment.status === 'PAYMENT_EXTERNAL_PENDING'
          }
          user={handlingBooking.user}
        />
      )}

      {!!handlingBooking && (
        <ConfirmationModal
          isOpen={!!isDeleteConfirmationModalVisible}
          onClose={() => toggleDeleteConfirmationModal(undefined)}
          onConfirm={(reason) => deleteBooking(handlingBooking, reason)}
          title="Confirmar cancelamento"
          message="Deseja realmente cancelar?"
          reasonAlert="Motivo requerido, ultrapassou tempo mínimo para cancelamento antes do início do evento"
          isReasonRequired={handlingBooking.exceededCancellationDeadline}
          showReasonInput
          warningMessage={cancelBookingWarningMessage}
        />
      )}

      <HStack align="flex-end" spacing="4">
        <TableFilters
          filterBy={filterBy}
          filterByOptions={filterByOptions}
          filterInputType={filterBy === 'bookedDate' ? 'datepicker' : 'input'}
          onChangeFilterBy={onChangeFilterBy}
          onFilter={onFilter}
          onOrder={onOrder}
          onSort={(sort) =>
            !!onSort && onSort(sort as 'bookedDate' | 'createdAt')
          }
          order={order}
          sortBy={sortBy}
          sortByOptions={sortByOptions}
        />

        <HStack align="flex-end" justifyContent="space-between" flex={1}>
          <ButtonGroup spacing="6">
            <HStack>
              <Switch
                name="showPending"
                id="showPending"
                isChecked={showPending}
                onChange={onShowPending}
                isDisabled={showSessionBlocks}
              />
              <Text htmlFor="showPending">Reservas pendentes</Text>
            </HStack>

            <HStack>
              <Switch
                name="showPaymentPending"
                id="showPaymentPending"
                isChecked={showPaymentPending}
                onChange={onShowPaymentPending}
                isDisabled={showSessionBlocks}
              />
              <Text htmlFor="showPaymentPending">Pagamentos pendentes</Text>
            </HStack>

            {!!onShowSessionBlocks && (
              <HStack>
                <Switch
                  name="showSessionBlocks"
                  id="showSessionBlocks"
                  isChecked={showSessionBlocks}
                  onChange={onShowSessionBlocks}
                />
                <Text htmlFor="showSessionBlocks">Paralisações</Text>
              </HStack>
            )}

            {toggleShowMyBookingsOnly && (
              <HStack>
                <Switch
                  name="showOnlyMyBookings"
                  id="showOnlyMyBookings"
                  isChecked={showMyBookingsOnly}
                  onChange={toggleShowMyBookingsOnly}
                />
                <Text htmlFor="showOnlyMyBookings">Minhas reservas</Text>
              </HStack>
            )}
          </ButtonGroup>

          {!!onToggleBookingsTypeFilter && (
            <VStack>
              <Text>Reservas</Text>

              <RadioGroup
                onChange={handleToggleBookingsTypeFilter}
                value={bookingsTypeFilter}
              >
                <HStack>
                  <Radio defaultChecked value="memberBookings">
                    Membro
                  </Radio>
                  <Radio value="guestsBookings">Convidados</Radio>
                </HStack>
              </RadioGroup>
            </VStack>
          )}
        </HStack>
      </HStack>

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            {showSessionBlocks ? (
              <>
                <Th>Atividade</Th>
                <Th>Membro</Th>
                <Th>Experiência do usuário</Th>
                <Th>Local</Th>
                <Th textAlign="center">Horário reservado</Th>
                <Th textAlign="center">Reservado em</Th>
                <Th>Tipo</Th>
                <Th>Modalidade</Th>
                <Th textAlign="center">Lado</Th>
                <Th textAlign="center">Preço</Th>
                <Th>Paralisação</Th>
                <Th textAlign="right" w="8" />
              </>
            ) : (
              <>
                {!!showActivityColumn && <Th>Atividade</Th>}

                {!!showMemberColumns && (
                  <>
                    <Th>Membro</Th>

                    <Th>Experiência do usuário</Th>
                  </>
                )}

                <Th>Local</Th>

                <Th textAlign="center">Horário reservado</Th>

                <Th textAlign="center">Reservado em</Th>

                {!showPending && <Th textAlign="center">Cancelado em</Th>}

                <Th>Status pagamento</Th>

                <Th>Checkins</Th>

                <Th textAlign="right" w="8" />
              </>
            )}
          </Tr>
        </Thead>

        <Tbody>
          {showSessionBlocks
            ? sessionBlockReservations.map((reservation) => {
                const blockReason =
                  reservation.block.reason === 'OTHER'
                    ? reservation.block.customReason
                    : sessionBlockReasonLabels[reservation.block.reason];

                return (
                  <Tr key={`${reservation.type}-${reservation.id}`}>
                    <Td fontWeight="bold">{reservation.activity.name}</Td>
                    <Td>{reservation.user.name}</Td>
                    <Td>
                      {typeof reservation.user.experience === 'number'
                        ? experienceLevelsTitleMap.get(
                            reservation.user.experience,
                          )
                        : 'Não avaliado'}
                    </Td>
                    <Td>{reservation.spot.name}</Td>
                    <Td textAlign="center">
                      <Box>
                        <Text>
                          {formatSessionBlockReservationDate(reservation)}
                        </Text>
                        <Text>
                          {`${formatSessionBlockReservationTime(
                            reservation.reservation.startTime,
                          )} - ${formatSessionBlockReservationTime(
                            reservation.reservation.endTime,
                          )}`}
                        </Text>
                      </Box>
                    </Td>
                    <Td textAlign="center">
                      <Text>
                        {formatSessionBlockCreatedAt(reservation).split('-')[0]}
                      </Text>
                      <Text>
                        {formatSessionBlockCreatedAt(reservation).split('-')[1]}
                      </Text>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={
                          reservation.type === 'BOOKING' ? 'blue' : 'cyan'
                        }
                      >
                        {reservation.type === 'BOOKING' ? 'Reserva' : 'Fila'}
                      </Badge>
                    </Td>
                    <Td>{reservation.modality?.title ?? '-'}</Td>
                    <Td textAlign="center">
                      {reservation.schedule.waveSide
                        ? WaveSideEnum[reservation.schedule.waveSide]
                        : '-'}
                    </Td>
                    <Td textAlign="center">
                      {formatPrice(reservation.reservation.price)}
                    </Td>
                    <Td>
                      <Box>
                        <Badge
                          colorScheme={
                            sessionBlockBookingStatusColorSchemes[
                              reservation.impactStatus
                            ]
                          }
                        >
                          {
                            sessionBlockBookingStatusLabels[
                              reservation.impactStatus
                            ]
                          }
                        </Badge>
                        {!!blockReason && (
                          <Text fontSize="sm" mt="1">
                            {blockReason}
                          </Text>
                        )}
                      </Box>
                    </Td>
                    <Td textAlign="right">
                      {reservation.type === 'BOOKING' &&
                        reservation.impactStatus === 'PENDING_CONFIRMATION' && (
                          <ButtonGroup>
                            <IconButton
                              aria-label="confirm-session-block-booking"
                              size="sm"
                              icon={
                                <Icon as={RiCheckboxCircleLine} fontSize="16" />
                              }
                              colorScheme="green"
                              onClick={() =>
                                handleConfirmAffectedReservation(reservation)
                              }
                            />

                            <IconButton
                              aria-label="decline-session-block-booking"
                              size="sm"
                              icon={
                                <Icon as={RiCloseCircleLine} fontSize="16" />
                              }
                              colorScheme="red"
                              onClick={() =>
                                handleDeclineAffectedReservation(reservation)
                              }
                            />
                          </ButtonGroup>
                        )}
                    </Td>
                  </Tr>
                );
              })
            : bookings.map((booking) => {
                const sessionBlockBooking = booking.sessionBlockBookings?.[0];
                const sessionBlockReason =
                  getSessionBlockReason(sessionBlockBooking);
                const isPaymentButtonDisabled =
                  !booking.payment ||
                  ![
                    'WAITING',
                    'FAILED',
                    'REJECTED',
                    'PAYMENT_EXTERNAL_PENDING',
                  ].includes(booking.payment.status) ||
                  !authenticatedAssignment?.featureGroup.features.some(
                    (feature) =>
                      [
                        'BOOKING_PAYMENT_FULL_ACCESS',
                        'BOOKING_PAYMENT_WRITE_ACCESS',
                      ].includes(feature.key),
                  );

                let paymentButtonColorScheme = isPaymentButtonDisabled
                  ? 'green'
                  : 'red';

                if (booking.payment?.status === 'PAYMENT_EXTERNAL_PENDING') {
                  paymentButtonColorScheme = 'yellow';
                }

                const userOnboard = booking.user.onboards.find(
                  (onboard) => onboard.ventureId === authenticatedVenture?.id,
                );

                return (
                  <Tr key={booking.id}>
                    {!!showActivityColumn && (
                      <Td fontWeight="bold">
                        {booking.activitySchedule.activity.name}
                      </Td>
                    )}

                    {!!showMemberColumns && (
                      <>
                        <Td>{booking.user.name}</Td>

                        <Td>
                          {userOnboard?.onboardType === 'COMPLETED'
                            ? experienceLevelsTitleMap.get(
                                userOnboard.experience,
                              )
                            : 'Não avaliado'}
                        </Td>
                      </>
                    )}

                    <Td>{booking.activitySchedule.activity.spot.name}</Td>

                    <Td textAlign="center">
                      <Box>
                        <Text>{booking.formattedBookedDate}</Text>
                        <Text>{`${booking.formattedStartTime} - ${booking.formattedEndTime}`}</Text>
                      </Box>
                    </Td>

                    <Td textAlign="center">
                      <Text>{booking.formattedCreatedAt.split('-')[0]}</Text>
                      <Text>{booking.formattedCreatedAt.split('-')[1]}</Text>
                    </Td>

                    {!showPending && !showSessionBlocks && (
                      <Td textAlign="center">
                        <Text>
                          {booking.formattedDeletedAt?.split('-')[0] || '-'}
                        </Text>
                        <Text>{booking.formattedDeletedAt?.split('-')[1]}</Text>
                      </Td>
                    )}

                    {showSessionBlocks && (
                      <Td>
                        {sessionBlockBooking ? (
                          <Box>
                            <Badge
                              colorScheme={
                                sessionBlockBookingStatusColorSchemes[
                                  sessionBlockBooking.status
                                ]
                              }
                            >
                              {
                                sessionBlockBookingStatusLabels[
                                  sessionBlockBooking.status
                                ]
                              }
                            </Badge>
                            {!!sessionBlockReason && (
                              <Text fontSize="sm" mt="1">
                                {sessionBlockReason}
                              </Text>
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </Td>
                    )}

                    <Td>
                      {booking.payment
                        ? PrettyBookingPaymentStatus[booking.payment.status]
                        : 'Isento'}
                    </Td>

                    <Td>
                      {booking.checkins.map((checkin) => (
                        <Text fontSize="sm">{`${checkin.user.name} - ${checkin.formattedCreatedAt}`}</Text>
                      ))}
                    </Td>

                    <Td textAlign="right">
                      <ButtonGroup>
                        {!booking.deletedAt &&
                          !!onCheckin &&
                          (!booking.payment ||
                            [
                              'APPROVED',
                              'PAYMENT_EXTERNAL_PENDING',
                              'PAYMENT_EXTERNAL',
                              'PAYMENT_HOTEL',
                            ].includes(booking.payment.status)) &&
                          authenticatedAssignment?.featureGroup.features.some(
                            (feature) =>
                              [
                                'BOOKING_CHECKIN_FULL_ACCESS',
                                'BOOKING_CHECKIN_WRITE_ACCESS',
                              ].includes(feature.key),
                          ) && (
                            <IconButton
                              aria-label="Checkins"
                              size="sm"
                              icon={
                                <Icon as={RiLoginCircleLine} fontSize="16" />
                              }
                              fontSize="16"
                              colorScheme="green"
                              onClick={() => {
                                setHandlingBooking(booking);
                                handleTooggleCheckinConfirmationModal();
                              }}
                            />
                          )}

                        {!!booking.payment &&
                          !booking.deletedAt &&
                          !showSessionBlocks &&
                          !!onPay &&
                          authenticatedAssignment?.featureGroup.features.some(
                            (feature) =>
                              [
                                'BOOKING_PAYMENT_FULL_ACCESS',
                                'BOOKING_PAYMENT_WRITE_ACCESS',
                              ].includes(feature.key),
                          ) && (
                            <IconButton
                              aria-label="Payment"
                              size="sm"
                              icon={
                                <Icon
                                  as={RiMoneyDollarCircleLine}
                                  fontSize="18"
                                />
                              }
                              fontSize="16"
                              colorScheme={paymentButtonColorScheme}
                              color="white"
                              disabled={isPaymentButtonDisabled}
                              onClick={() => togglePaymentModal(booking)}
                            />
                          )}

                        <LinkButton
                          px={0}
                          to={{
                            pathname: '/bookings/details',
                            state: {
                              bookingId: booking.id,
                            },
                          }}
                        >
                          <Icon as={RiEyeLine} fontSize="16" />
                        </LinkButton>

                        {booking.isCancelable &&
                          !showSessionBlocks &&
                          authenticatedAssignment?.featureGroup.features.some(
                            (feature) =>
                              [
                                'BOOKING_FULL_ACCESS',
                                'BOOKING_DELETE_ACCESS',
                              ].includes(feature.key),
                          ) && (
                            <IconButton
                              aria-label="delete-booking"
                              size="sm"
                              icon={<Icon as={RiDeleteBinLine} fontSize="16" />}
                              colorScheme="red"
                              onClick={() =>
                                toggleDeleteConfirmationModal(booking)
                              }
                            />
                          )}

                        {showSessionBlocks &&
                          sessionBlockBooking?.status ===
                            'PENDING_CONFIRMATION' &&
                          booking.userId === authenticatedUser?.id && (
                            <>
                              <IconButton
                                aria-label="confirm-session-block-booking"
                                size="sm"
                                icon={
                                  <Icon
                                    as={RiCheckboxCircleLine}
                                    fontSize="16"
                                  />
                                }
                                colorScheme="green"
                                onClick={() =>
                                  handleConfirmSessionBlockBooking(
                                    booking,
                                    sessionBlockBooking,
                                  )
                                }
                              />

                              <IconButton
                                aria-label="decline-session-block-booking"
                                size="sm"
                                icon={
                                  <Icon as={RiCloseCircleLine} fontSize="16" />
                                }
                                colorScheme="red"
                                onClick={() =>
                                  handleDeclineSessionBlockBooking(
                                    booking,
                                    sessionBlockBooking,
                                  )
                                }
                              />
                            </>
                          )}
                      </ButtonGroup>
                    </Td>
                  </Tr>
                );
              })}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
